// Pure scoring logic for battle mode.
//
// Victory Points follow the Warhammer: The Old World matched play rules
// (and the halved "battle-march" composition variant, matching game-view):
//   - Unit destroyed or fled the table: full unit points
//   - Unit fleeing at game end: half points
//   - Unit at or below 25% starting strength: half points
//   - Enemy General slain: +100 (battle-march: +50)
//   - Enemy Battle Standard Bearer slain: +50 (battle-march: +25)
//   - Per captured standard: +50 (battle-march: +25)

export const UNIT_STATUS = {
  ACTIVE: "active",
  QUARTER: "quarter", // at or below 25% strength
  FLEEING: "fleeing",
  FLED: "fled", // fled the table
  DESTROYED: "destroyed",
};

// VP an opponent earns from a single unit given its status.
export const getUnitBattleVP = ({ status, points }) => {
  switch (status) {
    case UNIT_STATUS.DESTROYED:
    case UNIT_STATUS.FLED:
      return points;
    case UNIT_STATUS.FLEEING:
    case UNIT_STATUS.QUARTER:
      return Math.round(points / 2);
    default:
      return 0;
  }
};

// VP a side earns from the damage tracked on the OTHER side.
// `unitStates`: { [unitId]: { status } }
// `unitPointsById`: { [unitId]: points }
export const getSideVP = ({
  unitStates = {},
  unitPointsById = {},
  banners = 0,
  scenarioPoints = 0,
  generalDead = false,
  bsbDead = false,
  compositionRule,
}) => {
  const isBattleMarch = compositionRule === "battle-march";
  const bannerVP = isBattleMarch ? 25 : 50;
  const generalVP = isBattleMarch ? 50 : 100;
  const bsbVP = isBattleMarch ? 25 : 50;

  let vp = 0;

  Object.entries(unitStates).forEach(([unitId, unitState]) => {
    vp += getUnitBattleVP({
      status: unitState.status,
      points: unitPointsById[unitId] || unitState.points || 0,
    });
  });

  vp += banners * bannerVP;
  vp += scenarioPoints;
  vp += generalDead ? generalVP : 0;
  vp += bsbDead ? bsbVP : 0;

  return vp;
};

// Map a VP result onto Battle Points using a scoring pack.
// Pack bracket format: sorted ascending by maxDiff, last entry may omit
// maxDiff (catch-all): { maxDiff?, winner, loser }
export const getBattlePoints = ({ playerVP, opponentVP, pack }) => {
  if (!pack || !pack.brackets?.length) {
    return { player: 0, opponent: 0, bracket: null };
  }

  const diff = Math.abs(playerVP - opponentVP);
  const bracket =
    pack.brackets.find(
      ({ maxDiff }) => maxDiff === undefined || diff <= maxDiff,
    ) || pack.brackets[pack.brackets.length - 1];

  const playerWinning = playerVP >= opponentVP;

  return {
    player: playerWinning ? bracket.winner : bracket.loser,
    opponent: playerWinning ? bracket.loser : bracket.winner,
    bracket,
  };
};
